import styled, { keyframes } from "styled-components";
import image from "../../../../../assets/img/contactless.jpg"; // swap to merchant image
import { blueColor, goldColor } from "../../../../styled/Theme";
import {
  WalletOutlined,
  BellOutlined,
  TeamOutlined,
  NodeIndexOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";

const features = [
  {
    num: "01",
    icon: <WalletOutlined />,
    title: "Centralised Payment Acceptance",
    body: "Unified settlement visibility across all payment types, channels, and branches — in real time.",
    tag: "Payments",
  },
  {
    num: "02",
    icon: <BellOutlined />,
    title: "Automated Customer Notifications",
    body: "Instant alerts for payments, refunds, and promotions delivered across in-app, SMS, and email.",
    tag: "Comms",
  },
  {
    num: "03",
    icon: <TeamOutlined />,
    title: "Secure Staff Communication",
    body: "Role-based controls ensure the right message reaches the right team, every time.",
    tag: "Access",
  },
  {
    num: "04",
    icon: <NodeIndexOutlined />,
    title: "Broadcast Across Branches",
    body: "Push service updates, policy changes, and campaigns to every location simultaneously.",
    tag: "Scale",
  },
];

const metrics = [
  { value: "3.2s", label: "Avg. settlement time" },
  { value: "99.9%", label: "Notification delivery" },
  { value: "∞", label: "Branch scalability" },
];

export default function AgentsMerchant() {
  return (
    <Wrapper>
      {/* ── Top eyebrow bar ── */}
      <EyebrowBar className="container">
        <EyebrowPill
          data-aos="fade-right"
          data-aos-duration="600"
          data-aos-delay="50"
        >
          <EyebrowDot />
          For Merchants &amp; Agents
        </EyebrowPill>
        <EyebrowLine />
      </EyebrowBar>

      {/* ── Main grid ── */}
      <MainGrid className="container">
        {/* Left: headline + features */}
        <LeftCol>
          <Headline
            data-aos="fade-up"
            data-aos-duration="700"
            data-aos-delay="100"
          >
            Operate <AccentWord>Efficiently.</AccentWord>
            <br />
            Communicate <AccentWord>Confidently.</AccentWord>
          </Headline>

          <Subline
            data-aos="fade-up"
            data-aos-duration="600"
            data-aos-delay="180"
          >
            Revenue management and customer communication — combined into a
            single, intelligent merchant layer.
          </Subline>

          <FeatureList>
            {features.map((f, i) => (
              <FeatureRow
                key={i}
                data-aos="fade-up"
                data-aos-duration="600"
                data-aos-delay={260 + i * 90}
              >
                <FeatureNum>{f.num}</FeatureNum>
                <FeatureContent>
                  <FeatureTopRow>
                    <FeatureIconWrap>{f.icon}</FeatureIconWrap>
                    <FeatureTitle>{f.title}</FeatureTitle>
                    <FeatureTag>{f.tag}</FeatureTag>
                  </FeatureTopRow>
                  <FeatureBody>{f.body}</FeatureBody>
                </FeatureContent>
              </FeatureRow>
            ))}
          </FeatureList>

          <CtaRow
            data-aos="fade-up"
            data-aos-duration="600"
            data-aos-delay="660"
          >
            <CtaButton>
              Get Started <ArrowRightOutlined />
            </CtaButton>
            <CtaLink>View merchant docs</CtaLink>
          </CtaRow>
        </LeftCol>

        {/* Right: image stack + metric pills */}
        <RightCol
          data-aos="fade-left"
          data-aos-duration="800"
          data-aos-delay="200"
        >
          <ImageStack>
            <ImageCardBack />
            <ImageCardMain>
              <HeroImage src={image} alt="Merchant using OnePayX" />
              <ImageGradient />
            </ImageCardMain>

            {/* Floating metric pills */}
            <MetricPill $top="18px" $right="-28px">
              <MetricValue>{metrics[0].value}</MetricValue>
              <MetricLabel>{metrics[0].label}</MetricLabel>
            </MetricPill>

            <MetricPill $bottom="80px" $left="-32px">
              <MetricValue>{metrics[1].value}</MetricValue>
              <MetricLabel>{metrics[1].label}</MetricLabel>
            </MetricPill>

            {/* Status card */}
            <StatusCard>
              <StatusDot />
              <StatusText>
                <StatusTitle>Live Processing</StatusTitle>
                <StatusSub>All systems operational</StatusSub>
              </StatusText>
            </StatusCard>
          </ImageStack>

          {/* Metric row below image */}
          <MetricStrip
            data-aos="fade-up"
            data-aos-duration="600"
            data-aos-delay="500"
          >
            {metrics.map((m, i) => (
              <MetricStripItem key={i}>
                <MetricStripValue>{m.value}</MetricStripValue>
                <MetricStripLabel>{m.label}</MetricStripLabel>
                {i < metrics.length - 1 && <MetricStripDivider />}
              </MetricStripItem>
            ))}
          </MetricStrip>
        </RightCol>
      </MainGrid>

      {/* ── Bottom tag strip ── */}
      <TagStrip className="container">
        <TagStripInner
          data-aos="fade-up"
          data-aos-duration="500"
          data-aos-delay="100"
        >
          {[
            "POS Integration",
            "Settlement Reports",
            "Refund Management",
            "Branch Comms",
            "Customer Alerts",
            "Role Controls",
            "Audit Logs",
          ].map((t, i) => (
            <Tag key={i}>{t}</Tag>
          ))}
        </TagStripInner>
      </TagStrip>
    </Wrapper>
  );
}

/* ── Animations ── */
const floatUp = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-6px); }
`;

/* ── Styles ── */

const Wrapper = styled.section`
  background: #f8fafc;
  padding-bottom: 0;
  overflow: hidden;
`;

const EyebrowBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 20px;
  padding-top: 56px;
  padding-bottom: 40px;
`;

const EyebrowPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 1.1px;
  text-transform: uppercase;
  color: ${blueColor};
  background: ${blueColor}0f;
  border: 1px solid ${blueColor}28;
  border-radius: 20px;
  padding: 4px 14px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const EyebrowDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${goldColor};
  flex-shrink: 0;
`;

const EyebrowLine = styled.div`
  flex: 1;
  height: 1px;
  background: linear-gradient(to right, ${blueColor}30, transparent);
`;

/* Main grid */
const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 420px;
  gap: 64px;
  align-items: flex-start;
  padding: 0 20px;
  padding-bottom: 64px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    gap: 48px;
  }
`;

/* Left col */
const LeftCol = styled.div``;

const Headline = styled.h2`
  font-size: 38px;
  font-weight: 900;
  color: #0a1628;
  letter-spacing: -1.5px;
  line-height: 1.12;
  margin: 0 0 16px;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const AccentWord = styled.span`
  color: ${blueColor};
`;

const Subline = styled.p`
  font-size: 15.5px;
  color: #64748b;
  line-height: 1.65;
  margin: 0 0 36px;
  max-width: 480px;
`;

/* Feature rows */
const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  border-top: 1px solid #e2e8f0;
`;

const FeatureRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 20px 0;
  border-bottom: 1px solid #e2e8f0;
  transition: background 0.2s;
  cursor: default;

  &:hover {
    background: transparent;

    ${() => FeatureNum} {
      color: ${blueColor};
    }
  }
`;

const FeatureNum = styled.div`
  font-size: 11px;
  font-weight: 800;
  color: #cbd5e1;
  letter-spacing: 1px;
  padding-top: 3px;
  width: 24px;
  flex-shrink: 0;
  transition: color 0.2s;
`;

const FeatureContent = styled.div`
  flex: 1;
`;

const FeatureTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 5px;
`;

const FeatureIconWrap = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 7px;
  background: ${blueColor}12;
  color: ${blueColor};
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const FeatureTitle = styled.div`
  font-size: 14.5px;
  font-weight: 700;
  color: #0f172a;
  flex: 1;
`;

const FeatureTag = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: ${goldColor};
  background: ${goldColor}15;
  border-radius: 4px;
  padding: 2px 7px;
`;

const FeatureBody = styled.p`
  font-size: 13.5px;
  color: #64748b;
  line-height: 1.6;
  margin: 0;
`;

const CtaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-top: 32px;
`;

const CtaButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: ${blueColor};
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  border: none;
  border-radius: 10px;
  padding: 12px 24px;
  cursor: pointer;
  transition: all 0.22s;

  &:hover {
    background: #1557a0;
    gap: 12px;
  }
`;

const CtaLink = styled.a`
  font-size: 13.5px;
  font-weight: 600;
  color: #64748b;
  text-decoration: underline;
  text-underline-offset: 3px;
  cursor: pointer;

  &:hover {
    color: ${blueColor};
  }
`;

/* Right col */
const RightCol = styled.div`
  @media (max-width: 980px) {
    max-width: 480px;
  }
`;

const ImageStack = styled.div`
  position: relative;
  margin-bottom: 20px;
`;

const ImageCardBack = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  right: -8px;
  bottom: -8px;
  background: ${blueColor}15;
  border-radius: 22px;
  border: 1px solid ${blueColor}20;
`;

const ImageCardMain = styled.div`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 24px 64px rgba(10, 30, 80, 0.18);
`;

const HeroImage = styled.img`
  width: 100%;
  height: 380px;
  object-fit: cover;
  display: block;
  transition: transform 0.7s ease;

  ${ImageCardMain}:hover & {
    transform: scale(1.03);
  }

  @media (max-width: 580px) {
    height: 260px;
  }
`;

const ImageGradient = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    180deg,
    transparent 50%,
    rgba(7, 20, 55, 0.55) 100%
  );
`;

/* Floating metric pills */
const MetricPill = styled.div<{
  $top?: string;
  $bottom?: string;
  $left?: string;
  $right?: string;
}>`
  position: absolute;
  ${(p) => p.$top && `top: ${p.$top};`}
  ${(p) => p.$bottom && `bottom: ${p.$bottom};`}
  ${(p) => p.$left && `left: ${p.$left};`}
  ${(p) => p.$right && `right: ${p.$right};`}
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 12px;
  padding: 10px 16px;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.13);
  z-index: 3;
  animation: ${floatUp} 3.5s ease-in-out infinite;

  &:nth-child(3) {
    animation-delay: 1.2s;
  }
`;

const MetricValue = styled.div`
  font-size: 20px;
  font-weight: 900;
  color: ${blueColor};
  letter-spacing: -0.5px;
  line-height: 1;
`;

const MetricLabel = styled.div`
  font-size: 10.5px;
  color: #94a3b8;
  font-weight: 500;
  margin-top: 2px;
  white-space: nowrap;
`;

/* Status card */
const StatusCard = styled.div`
  position: absolute;
  bottom: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 9px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(10px);
  border-radius: 10px;
  padding: 8px 14px;
  z-index: 3;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.1);
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 0 3px #22c55e30;
  flex-shrink: 0;
`;

const StatusText = styled.div``;

const StatusTitle = styled.div`
  font-size: 12.5px;
  font-weight: 700;
  color: #0f172a;
`;

const StatusSub = styled.div`
  font-size: 10.5px;
  color: #94a3b8;
`;

/* Metric strip */
const MetricStrip = styled.div`
  display: flex;
  align-items: stretch;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const MetricStripItem = styled.div`
  flex: 1;
  padding: 16px;
  position: relative;
  text-align: center;
`;

const MetricStripValue = styled.div`
  font-size: 22px;
  font-weight: 900;
  color: ${blueColor};
  letter-spacing: -1px;
`;

const MetricStripLabel = styled.div`
  font-size: 10.5px;
  color: #94a3b8;
  font-weight: 500;
  margin-top: 3px;
`;

const MetricStripDivider = styled.div`
  position: absolute;
  right: 0;
  top: 20%;
  height: 60%;
  width: 1px;
  background: #e2e8f0;
`;

/* Tag strip */
const TagStrip = styled.div`
  padding: 0 20px;
  padding-bottom: 56px;
`;

const TagStripInner = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
`;

const Tag = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 5px 13px;
  transition: all 0.2s;
  cursor: default;

  &:hover {
    border-color: ${blueColor}50;
    color: ${blueColor};
    background: ${blueColor}06;
  }
`;
